import api from './api';

/**
 * Stroom 3: projecten en mijlpalen (backend: /api/projecten).
 *
 * HR stelt mijlpalen met een budget vast, de professional schrijft daarbinnen
 * uren, en pas na een klik op "Check & Fiat" ontstaat er een factuur.
 */

export type MijlpaalStatus =
  | 'PLANNED'
  | 'SUBMITTED'
  | 'APPROVED'
  | 'REJECTED'
  | 'INVOICED'
  // Long-term B2B Project Engine & Budget-Grendel (migratie 136)
  | 'LOCKED_PENDING'
  | 'ACTIVE_RUNNING'
  | 'CAP_REACHED_PENDING_APPROVAL'
  | 'COMPLETED_WAITING_FOR_FIAT'
  | 'ARCHIVED';

export interface TeFiatterenMijlpaal {
  id: string;
  volgnummer: number;
  titel: string;
  budget_bedrag: string;
  budget_uren: string | null;
  uurtarief: string | null;
  geschreven_uren: string;
  submitted_at: string | null;
  project_code: string;
  project_titel: string;
  kvk_handelsnaam: string | null;
}

export interface ProfessionalMijlpaal {
  id: string;
  volgnummer: number;
  titel: string;
  budget_bedrag: string;
  budget_uren: string | null;
  uurtarief: string | null;
  geschreven_uren: string;
  status: MijlpaalStatus;
  afwijzing_reden: string | null;
  project_code: string;
  project_titel: string;
  /** TRAJECT = OvO-traject (114), WIZARD = Project-Onboarding Wizard met harde uren-cap (136). */
  project_bron?: 'TRAJECT' | 'WIZARD';
  project_status?: string;
  start_datum?: string | null;
  eind_datum?: string | null;
  extra_uren_toegekend?: string | null;
  geactiveerd_op?: string | null;
  cap_bereikt_op?: string | null;
  afgerond_op?: string | null;
  approved_at?: string | null;
  werkgever_voornaam?: string | null;
  werkgever_achternaam?: string | null;
  werkgever_bedrijf?: string | null;
}

// ─── HR-cockpit ──────────────────────────────────────────────────────────────

export const fetchFiatWachtrij = () => api.get('/projecten/fiat-wachtrij');

export const fiatteerMijlpaal = (milestoneId: string) =>
  api.post(`/projecten/mijlpalen/${milestoneId}/fiat`);

export const wijsMijlpaalAf = (milestoneId: string, reden: string) =>
  api.post(`/projecten/mijlpalen/${milestoneId}/afwijzen`, { reden });

export const factureerMijlpalen = (jaar?: number, maand?: number) =>
  api.post('/projecten/factureren', { jaar, maand });

export const maakProject = (payload: {
  professional_id: string;
  titel: string;
  omschrijving?: string;
  start_datum?: string;
}) => api.post('/projecten', payload);

export const maakMijlpaal = (
  projectId: string,
  payload: { volgnummer: number; titel: string; budget_bedrag: number; budget_uren?: number; uurtarief?: number }
) => api.post(`/projecten/${projectId}/mijlpalen`, payload);

// ─── Professional ────────────────────────────────────────────────────────────

export const fetchEigenMijlpalen = () => api.get('/projecten/professional/mijlpalen');

/** [Mijlpaal afronden] — Budget-Grendel scenario B: → COMPLETED_WAITING_FOR_FIAT. */
export const rondMijlpaalAf = (milestoneId: string) => api.post(`/projecten/mijlpalen/${milestoneId}/afronden`);

export const schrijfUrenOpMijlpaal = (
  milestoneId: string,
  payload: { uren: number; datum: string; omschrijving: string }
) => api.post(`/projecten/mijlpalen/${milestoneId}/uren`, payload);

// ─── Projecttraject (stappen 1-7, /api/projecten/traject) ────────────────────

export type ProjectStatus =
  | 'AANGEVRAAGD'
  | 'GEACCEPTEERD'
  | 'PROPOSAL_SUBMITTED'
  | 'CONTRACT_PENDING'
  | 'ACTIEF'
  | 'AFGEROND'
  | 'GEANNULEERD'
  | 'VERLOPEN'
  | 'AFGEWEZEN';

export type PoolStatus = 'GROEN' | 'BLAUW' | 'ORANJE' | 'ROOD';

export interface ExpertOverzicht {
  professional_id: string;
  naam: string;
  bedrijfsnaam: string | null;
  pool_status: PoolStatus;
  werkt_alleen_online: boolean;
  reisradius_km: number | null;
  afstand_km: number | null;
  binnen_radius: boolean;
  capaciteit_beschikbaar: boolean;
}

export interface TrajectMilestone {
  id: string;
  volgnummer: number;
  titel: string;
  budget_uren: string | null;
  uurtarief: string | null;
  budget_bedrag: string;
  geschreven_uren: string;
  status: 'PROPOSED' | MijlpaalStatus;
}

export interface TrajectProject {
  id: string;
  project_code: string;
  titel: string;
  omschrijving: string | null;
  status: ProjectStatus;
  professional_type: string;
  afstand_km: string | null;
  aanvraag_verloopt_op: string | null;
  afwijzing_reden: string | null;
  first_name?: string;
  last_name?: string;
  invoice_bedrijfsnaam?: string | null;
  werkgever_naam?: string | null;
  milestones: TrajectMilestone[];
  kennismakingen: Array<{
    id: string;
    start_tijd: string;
    duur_minuten: number;
    video_url: string;
    status: string;
  }>;
  contract: {
    id: string;
    status: 'WACHT_OP_WERKGEVER' | 'WACHT_OP_PROFESSIONAL' | 'GETEKEND' | 'VERVALLEN';
    werkgever_getekend_op: string | null;
    professional_getekend_op: string | null;
    sealed_at: string | null;
  } | null;
  uitbreidingen: Array<{
    id: string;
    milestone_id: string;
    volgnummer: number;
    milestone_titel: string;
    extra_uren: string;
    extra_bedrag: string;
    platform_fee_bedrag: string;
    onderbouwing: string;
    status: 'EXTENSION_PENDING' | 'APPROVED' | 'REJECTED';
  }>;
}

export const fetchTrajectDisciplines = () => api.get('/projecten/traject/disciplines');

export const fetchExperts = (discipline: string, postcode: string) =>
  api.get('/projecten/traject/experts', { params: { discipline, postcode } });

export const maakProjectAanvraag = (payload: {
  professional_id: string;
  discipline: string;
  titel: string;
  omschrijving?: string;
  postcode: string;
}) => api.post('/projecten/traject/aanvraag', payload);

export const fetchEmployerProjecten = () => api.get('/projecten/traject/employer');
export const fetchProfessionalProjecten = () => api.get('/projecten/traject/professional');

export const accepteerAanvraag = (projectId: string) =>
  api.post(`/projecten/traject/${projectId}/accepteer`);
export const wijsAanvraagAf = (projectId: string, reden: string) =>
  api.post(`/projecten/traject/${projectId}/afwijzen`, { reden });

export const fetchKennismakingSlots = (projectId: string) =>
  api.get(`/projecten/traject/${projectId}/slots`);
export const boekKennismaking = (projectId: string, slotId: string, duurMinuten: 15 | 30) =>
  api.post(`/projecten/traject/${projectId}/kennismaking`, { slot_id: slotId, duur_minuten: duurMinuten });

export const dienVoorstelIn = (
  projectId: string,
  milestones: Array<{ titel: string; budget_uren: number; uurtarief: number }>
) => api.post(`/projecten/traject/${projectId}/voorstel`, { milestones });

export const accordeerVoorstel = (projectId: string) =>
  api.post(`/projecten/traject/${projectId}/accordeer-voorstel`);

export const fetchOvoContract = (contractId: string) =>
  api.get(`/projecten/traject/contract/${contractId}`);
export const vraagTekencode = (contractId: string) =>
  api.post(`/projecten/traject/contract/${contractId}/teken-code`);
export const ondertekenContract = (contractId: string, code: string) =>
  api.post(`/projecten/traject/contract/${contractId}/onderteken`, { code });

export const vraagUitbreidingAan = (milestoneId: string, extraUren: number, onderbouwing: string) =>
  api.post(`/projecten/traject/mijlpalen/${milestoneId}/uitbreiding`, {
    extra_uren: extraUren,
    onderbouwing,
  });
export const accordeerUitbreiding = (uitbreidingId: string) =>
  api.post(`/projecten/traject/uitbreidingen/${uitbreidingId}/accordeer`);
export const wijsUitbreidingAf = (uitbreidingId: string, reden: string) =>
  api.post(`/projecten/traject/uitbreidingen/${uitbreidingId}/afwijzen`, { reden });
